import { useImperativeHandle, useCallback } from 'react'

/**
 * Hook to expose abstract table methods via ref
 * These methods work regardless of the underlying table implementation
 *
 * @param {Object} ref - React ref from forwardRef
 * @param {Object} gridRef - Reference to the AG-Grid instance
 * @param {Object} options - Additional options
 * @param {Array} options.rows - All row data
 * @param {Array} options.selectedRows - Currently selected rows
 * @param {Function} options.onSelectionChange - Selection change handler
 */
export const useTableRef = (ref, gridRef, options = {}) => {
    const { rows = [], selectedRows = [], onSelectionChange } = options

    // Get all selected rows
    const getSelectedRows = useCallback(() => {
        if (gridRef?.current?.api) {
            const selectedNodes = gridRef.current.api.getSelectedNodes()
            return selectedNodes.map((node) => node.data)
        }
        return selectedRows
    }, [gridRef, selectedRows])

    // Select all rows
    const selectAll = useCallback(() => {
        if (gridRef?.current?.api) {
            gridRef.current.api.selectAll()
        } else if (onSelectionChange) {
            onSelectionChange([...rows])
        }
    }, [gridRef, rows, onSelectionChange])

    // Deselect all rows
    const deselectAll = useCallback(() => {
        if (gridRef?.current?.api) {
            gridRef.current.api.deselectAll()
        } else if (onSelectionChange) {
            onSelectionChange([])
        }
    }, [gridRef, onSelectionChange])

    // Refresh data
    const refresh = useCallback(() => {
        if (gridRef?.current?.api) {
            gridRef.current.api.refreshCells()
        }
    }, [gridRef])

    // Auto-size all columns
    const autoSizeColumns = useCallback(() => {
        if (gridRef?.current?.api) {
            const allColumnIds = gridRef.current.api.getColumns()?.map((column) => column.getId())

            if (allColumnIds?.length) {
                gridRef.current.api.autoSizeColumns(allColumnIds)
            }
        }
    }, [gridRef])

    // Size columns to fit container
    const sizeColumnsToFit = useCallback(() => {
        if (gridRef?.current?.api) {
            gridRef.current.api.sizeColumnsToFit()
        }
    }, [gridRef])

    // Export to CSV
    const exportToCsv = useCallback(
        (filename = 'export.csv') => {
            if (gridRef?.current?.api) {
                gridRef.current.api.exportDataAsCsv({
                    fileName: filename,
                })
            }
        },
        [gridRef]
    )

    // Get row data by index
    const getRowByIndex = useCallback(
        (index) => {
            if (gridRef?.current?.api) {
                const rowNode = gridRef.current.api.getRowNode(String(index))
                return rowNode?.data
            }
            return rows[index]
        },
        [gridRef, rows]
    )

    // Get all row data
    const getAllRows = useCallback(() => {
        if (gridRef?.current?.api) {
            const allRows = []
            gridRef.current.api.forEachNode((node) => {
                allRows.push(node.data)
            })
            return allRows
        }
        return rows
    }, [gridRef, rows])

    // Set filter on a column
    const setColumnFilter = useCallback(
        (field, filterValue) => {
            if (gridRef?.current?.api) {
                const filterInstance = gridRef.current.api.getFilterInstance(field)
                if (filterInstance) {
                    filterInstance.setModel({ filter: filterValue })
                    gridRef.current.api.onFilterChanged()
                }
            }
        },
        [gridRef]
    )

    // Clear all filters
    const clearFilters = useCallback(() => {
        if (gridRef?.current?.api) {
            gridRef.current.api.setFilterModel(null)
        }
    }, [gridRef])

    // Scroll to row
    const scrollToRow = useCallback(
        (index) => {
            if (gridRef?.current?.api) {
                gridRef.current.api.ensureIndexVisible(index)
            }
        },
        [gridRef]
    )

    // Get AG-Grid API (escape hatch for advanced users)
    const getGridApi = useCallback(() => {
        return gridRef?.current?.api || null
    }, [gridRef])

    // Expose methods via ref
    useImperativeHandle(
        ref,
        () => ({
            // Selection
            getSelectedRows,
            selectAll,
            deselectAll,

            // Data
            refresh,
            getAllRows,
            getRowByIndex,

            // Columns
            autoSizeColumns,
            sizeColumnsToFit,

            // Export
            exportToCsv,

            // Filters
            setColumnFilter,
            clearFilters,

            // Navigation
            scrollToRow,

            // Escape hatch (for advanced AG-Grid features)
            getGridApi,
        }),
        [
            getSelectedRows,
            selectAll,
            deselectAll,
            refresh,
            getAllRows,
            getRowByIndex,
            autoSizeColumns,
            sizeColumnsToFit,
            exportToCsv,
            setColumnFilter,
            clearFilters,
            scrollToRow,
            getGridApi,
        ]
    )
}
